import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check, Mail, Pencil, UserCircle2 } from "lucide-react";

const REMEMBER_KEY = "gbi_client";
export const loadRemembered = () => { try { return JSON.parse(localStorage.getItem(REMEMBER_KEY)) || null; } catch { return null; } };
export const saveRemembered = (c) => localStorage.setItem(REMEMBER_KEY, JSON.stringify({ first_name: c.first_name, last_name: c.last_name, email: c.email, mobile: c.mobile }));

export function EmailLinkForm({ nextPath, testid = "client-signin", initialEmail = "" }) {
  const [email, setEmail] = useState(initialEmail);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const send = async (e) => {
    e?.preventDefault(); setState("sending"); setError("");
    try { await api.post("/public/client/magic-link", { email, origin_url: window.location.origin, next_path: nextPath }); setState("sent"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); setState("idle"); }
  };
  if (state === "sent") return <p data-testid={`${testid}-sent`} className="text-sm text-emerald-700 flex items-center gap-2"><Check className="h-4 w-4" />Check {email} — tap the link to sign in.</p>;
  return (
    <form onSubmit={send}>
      <p className="text-xs text-stone-500 mb-2">We'll email you a one-tap sign-in link — no password needed.</p>
      <div className="flex gap-2">
        <Input type="email" required data-testid={`${testid}-email`} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="h-11 rounded-lg" />
        <Button type="submit" data-testid={`${testid}-send`} disabled={state === "sending" || !email} className="h-11 rounded-full bg-stone-900 shrink-0"><Mail className="h-4 w-4 mr-1" />{state === "sending" ? "Sending…" : "Send link"}</Button>
      </div>
      {error && <p className="text-xs text-rose-600 mt-1.5">{error}</p>}
    </form>
  );
}

function Field({ label, k, contact, setContact, type = "text", optional }) {
  return (
    <div><Label>{label}{optional && <span className="text-stone-400 font-normal"> (optional)</span>}</Label>
      <Input type={type} data-testid={`public-${k.replace("_", "-")}`} value={contact[k]} onChange={(e) => setContact((c) => ({ ...c, [k]: e.target.value }))}
        autoComplete={{ first_name: "given-name", last_name: "family-name", email: "email", mobile: "tel" }[k]} inputMode={k === "mobile" ? "tel" : undefined} className="mt-1.5 h-11 rounded-lg" /></div>
  );
}

export function ContactStep({ contact, setContact, account, slug }) {
  const complete = contact.first_name && contact.email && contact.mobile;
  const [editing, setEditing] = useState(!complete);
  const [showNote, setShowNote] = useState(!!contact.notes);
  const [showSignIn, setShowSignIn] = useState(false);
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4 space-y-3 shadow-sm" data-testid="contact-step">
      {!editing ? (
        <div className="flex items-start gap-3" data-testid="contact-summary">
          <UserCircle2 className="h-9 w-9 text-stone-400 shrink-0" />
          <div className="flex-1 min-w-0 text-sm"><div className="font-semibold">{contact.first_name} {contact.last_name}</div><div className="text-stone-600 truncate">{contact.mobile} · {contact.email}</div>
            {account && <div className="text-xs text-emerald-700 mt-0.5">Signed in</div>}</div>
          <button data-testid="contact-edit" onClick={() => setEditing(true)} className="text-sm underline flex items-center gap-1"><Pencil className="h-3.5 w-3.5" />Edit</button>
        </div>
      ) : (<>
        <div className="grid grid-cols-2 gap-3"><Field label="First name" k="first_name" contact={contact} setContact={setContact} /><Field label="Last name" k="last_name" contact={contact} setContact={setContact} optional /></div>
        <Field label="Mobile" k="mobile" contact={contact} setContact={setContact} type="tel" />
        <Field label="Email" k="email" contact={contact} setContact={setContact} type="email" />
      </>)}
      {showNote ? <div><Label>Note for the salon</Label><Textarea data-testid="public-notes" value={contact.notes} onChange={(e) => setContact((c) => ({ ...c, notes: e.target.value }))} className="mt-1.5 rounded-lg" rows={2} /></div>
        : <button data-testid="add-note" onClick={() => setShowNote(true)} className="text-sm text-stone-600 underline">+ Add a note</button>}
      {!account && !complete && (
        <div className="border-t border-stone-100 pt-3 text-sm">
          {showSignIn ? <EmailLinkForm nextPath={`/${slug}`} /> : <button data-testid="show-signin" onClick={() => setShowSignIn(true)} className="text-stone-600">Booked with us before? <span className="underline font-medium text-stone-900">Fill in my details</span></button>}
        </div>
      )}
    </div>
  );
}
